import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { categoriesAPI } from '../../services/api';

export const CONTENT_TYPES = [
  { value: 'bids',       label: 'Bids' },
  { value: 'blogs',      label: 'Blogs' },
  { value: 'vacancies',  label: 'Vacancies' },
  { value: 'bursaries',  label: 'Bursaries' },
  { value: 'projects',   label: 'Projects' },
  { value: 'gallery',    label: 'Gallery' },
] as const;

export type ContentType = typeof CONTENT_TYPES[number]['value'];

export interface Category {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  description?: string;
  applies_to: string; // comma-separated e.g. "bids,blogs"
  color: string;
  is_active: boolean | number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

interface CategoriesState {
  items: Category[];
  // per-contentType cache so BidForm doesn't need to refetch
  byContentType: Partial<Record<ContentType, Category[]>>;
  loading: boolean;
  loadingByType: Partial<Record<ContentType, boolean>>;
  error: string | null;
}

const initialState: CategoriesState = {
  items: [],
  byContentType: {},
  loading: false,
  loadingByType: {},
  error: null,
};

// ─── Thunks ────────────────────────────────────────────────────────────────

export const fetchCategories = createAsyncThunk(
  'categories/fetchAll',
  async (
    params: { applies_to?: ContentType; active?: boolean } = {},
    { rejectWithValue }
  ) => {
    try {
      const res = await categoriesAPI.getAll(params);
      return { data: res.data as Category[], applies_to: params.applies_to };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch categories');
    }
  }
);

export const createCategory = createAsyncThunk(
  'categories/create',
  async (
    payload: { name: string; description?: string; applies_to: string[]; color?: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await categoriesAPI.create(payload);
      return res.data.category as Category;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create category');
    }
  }
);

export const updateCategory = createAsyncThunk(
  'categories/update',
  async (
    { id, data }: {
      id: string;
      data: { name?: string; description?: string; applies_to?: string[]; color?: string; is_active?: boolean };
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await categoriesAPI.update(id, data);
      return res.data.category as Category;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update category');
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'categories/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await categoriesAPI.delete(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete category');
    }
  }
);

export const toggleCategory = createAsyncThunk(
  'categories/toggle',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await categoriesAPI.toggle(id);
      return { id, is_active: res.data.is_active as boolean };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to toggle category');
    }
  }
);

// ─── Helper: update item in array ─────────────────────────────────────────

const replaceItem = (items: Category[], updated: Category): Category[] =>
  items.map(i => (i.id === updated.id ? updated : i));

// ─── Slice ─────────────────────────────────────────────────────────────────

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    // Invalidate per-type cache (call after create/update/delete)
    invalidateCache(state) {
      state.byContentType = {};
    },
  },
  extraReducers: builder => {
    // fetchCategories
    builder
      .addCase(fetchCategories.pending, (state, action) => {
        const type = action.meta.arg.applies_to;
        if (type) {
          state.loadingByType[type] = true;
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        const type = action.payload.applies_to;
        if (type) {
          state.byContentType[type] = action.payload.data;
          state.loadingByType[type] = false;
        } else {
          state.items = action.payload.data;
          state.loading = false;
        }
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        const type = action.meta.arg.applies_to;
        if (type) {
          state.loadingByType[type] = false;
        } else {
          state.loading = false;
        }
        state.error = action.payload as string;
      });

    // createCategory
    builder
      .addCase(createCategory.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.byContentType = {}; // invalidate per-type cache
        state.error = null;
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // updateCategory
    builder
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.items = replaceItem(state.items, action.payload);
        state.byContentType = {};
        state.error = null;
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // deleteCategory
    builder
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.items = state.items.filter(i => i.id !== action.payload);
        state.byContentType = {};
        state.error = null;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // toggleCategory
    builder
      .addCase(toggleCategory.fulfilled, (state, action) => {
        const { id, is_active } = action.payload;
        const item = state.items.find(i => i.id === id);
        if (item) item.is_active = is_active;
        state.byContentType = {};
        state.error = null;
      })
      .addCase(toggleCategory.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError, invalidateCache } = categoriesSlice.actions;
export default categoriesSlice.reducer;